import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import {
  Link as LinkIcon,
  VpnKey,
  Settings,
  ExpandMore,
  Save,
  PlayCircleOutline,
  Visibility,
  VisibilityOff,
  Group,
  Add,
  Delete,
} from '@material-ui/icons';
import { CustomFieldsData, IntegrationData } from 'services/dataService';

import { useStyles } from './styles';

const PLATFORM_CONFIG = {
  senior: {
    title: 'Senior (SOAP)',
    formTitle: 'Nova Integração - Senior (SOAP)',
    description:
      'Integração para sincronização de colaboradores via endpoint SOAP.',
    authType: 'password',
    type: 'erp',
    direction: 'outbound',
    canTestConnection: true,
  },
  sults: {
    title: 'Sults',
    formTitle: 'Nova Integração - Sults',
    description:
      'Integração para sincronização de colaboradores via API Sults.',
    authType: 'token',
    type: 'api',
    direction: 'outbound',
    canTestConnection: true,
  },
  totvs: {
    title: 'TOTVS RM',
    formTitle: 'Nova Integração - TOTVS RM',
    description:
      'Integração para sincronização de colaboradores via API REST do TOTVS RM.',
    authType: 'token',
    type: 'erp',
    direction: 'outbound',
    canTestConnection: true,
  },
};

const EMPTY_CUSTOM_FIELD = {
  source_field: '',
  target_field: '',
};

const DEFAULT_FORM = {
  base_url: '',
  user: '',
  password: '',
  token: '',
  initial_password: '',
  use_cpf_as_password: false,
  send_welcome_emails: false,
  data_admissao_inicio: '',
  data_admissao_fim: '',
  data_demissao_inicio: '',
  data_demissao_fim: '',
  situacao: 'AD',
  numEmp: '1',
  abrTipCol: '1',
  iniPer: '01/01/2021',
  encryption: '0',
  mode: 'scheduled',
  days_sync_at: 1,
  active: true,
  auto_team_link: false,
  team_name_field: '',
  custom_fields: [EMPTY_CUSTOM_FIELD],
};

const getDefaultForm = (platform) => ({
  ...DEFAULT_FORM,
  base_url: '',
  mode: platform === 'sults' ? 'scheduled' : DEFAULT_FORM.mode,
});

const toSettings = (form, platform) => {
  if (platform === 'sults') {
    return { settings: [
      { fieldName: 'base_url', fieldValue: form.base_url || '' },
      { fieldName: 'token', fieldValue: form.token || '' },
      { fieldName: 'initial_password', fieldValue: form.initial_password || '' },

      { fieldName: 'use_cpf_as_password', fieldValue: Boolean(form.use_cpf_as_password) },
      { fieldName: 'team', fieldValue: form.auto_team_link ? form.team_name_field || '' : '' },
      { fieldName: 'custom_fields', fieldValue: (form.custom_fields || []).filter((field) => field.source_field && field.target_field) },
    ] };
  }

  if (platform === 'totvs') {
    return { settings: [
      { fieldName: 'base_url', fieldValue: form.base_url || '' },
      { fieldName: 'token', fieldValue: form.token || '' },
      { fieldName: 'initial_password', fieldValue: form.initial_password || '' },
      { fieldName: 'send_welcome_emails', fieldValue: Boolean(form.send_welcome_emails) },
      { fieldName: 'use_cpf_as_password', fieldValue: Boolean(form.use_cpf_as_password) },
      { fieldName: 'filters', fieldValue: {
        data_admissao_inicio: form.data_admissao_inicio || '',
        data_admissao_fim: form.data_admissao_fim || '',
        data_demissao_inicio: form.data_demissao_inicio || '',
        data_demissao_fim: form.data_demissao_fim || '',
        situacao: form.situacao || 'AD',
      } },
      { fieldName: 'team', fieldValue: form.auto_team_link ? form.team_name_field || '' : '' },
      { fieldName: 'custom_fields', fieldValue: (form.custom_fields || []).filter((field) => field.source_field && field.target_field) },
    ] };
  }

  const settings = [
    { fieldName: 'base_url', fieldValue: form.base_url || '' },

      { fieldName: 'use_cpf_as_password', fieldValue: Boolean(form.use_cpf_as_password) },
    { fieldName: 'initial_password', fieldValue: form.initial_password || '' },
    { fieldName: 'encryption', fieldValue: Number(form.encryption || 0) },
    { fieldName: 'parameters', fieldValue: { numEmp: form.numEmp || '1', abrTipCol: form.abrTipCol || '1', iniPer: form.iniPer || '01/01/2021' } },
    { fieldName: 'team', fieldValue: form.auto_team_link ? form.team_name_field || '' : '' },
  ];
  settings.splice(1, 0, { fieldName: 'user', fieldValue: form.user || '' }, { fieldName: 'password', fieldValue: form.password || '' });
  return { settings };
};

const getSettingValue = (settings, key) => {
  let normalizedSettings = settings;

  if (typeof normalizedSettings === 'string') {
    try {
      normalizedSettings = JSON.parse(normalizedSettings);
    } catch (error) {
      normalizedSettings = {};
    }
  }

  if (Array.isArray(normalizedSettings)) {
    const found = normalizedSettings.find((item) => item?.fieldName === key);
    return found ? found.fieldValue : undefined;
  }

  if (normalizedSettings && typeof normalizedSettings === 'object') {
    return normalizedSettings[key];
  }

  return undefined;
};

const normalizeCustomFields = (customFields) => {
  let normalizedCustomFields = customFields;

  if (typeof normalizedCustomFields === 'string') {
    try {
      normalizedCustomFields = JSON.parse(normalizedCustomFields);
    } catch (error) {
      normalizedCustomFields = [];
    }
  }

  if (
    !Array.isArray(normalizedCustomFields) ||
    !normalizedCustomFields.length
  ) {
    return [EMPTY_CUSTOM_FIELD];
  }

  return normalizedCustomFields.map((field) => ({
    source_field: field.source_field || '',
    target_field: field.target_field || '',
  }));
};

const fromIntegration = (integration) => {
  const settings = integration?.settings || {};
  const filters = getSettingValue(settings, 'filters') || {};
  const parameters = getSettingValue(settings, 'parameters') || {};
  let normalizedFilters = filters;
  let normalizedParameters = parameters;
  try {
    if (typeof normalizedFilters === 'string') normalizedFilters = JSON.parse(normalizedFilters);
    if (typeof normalizedParameters === 'string') normalizedParameters = JSON.parse(normalizedParameters);
  } catch (error) {
    normalizedFilters = {};
    normalizedParameters = {};
  }
  return {
    base_url: getSettingValue(settings, 'base_url') || '',
    user: getSettingValue(settings, 'user') || '',
    password: getSettingValue(settings, 'password') || '',
    token: getSettingValue(settings, 'token') || '',
    initial_password: getSettingValue(settings, 'initial_password') || '',
    use_cpf_as_password: Boolean(getSettingValue(settings, 'use_cpf_as_password')),
    send_welcome_emails: getSettingValue(settings, 'send_welcome_emails') === true || getSettingValue(settings, 'send_welcome_emails') === 'true',
    data_admissao_inicio: normalizedFilters.data_admissao_inicio || '',
    data_admissao_fim: normalizedFilters.data_admissao_fim || '',
    data_demissao_inicio: normalizedFilters.data_demissao_inicio || '',
    data_demissao_fim: normalizedFilters.data_demissao_fim || '',
    situacao: normalizedFilters.situacao || 'AD',
    numEmp: normalizedParameters.numEmp ? String(normalizedParameters.numEmp) : '1',
    abrTipCol: normalizedParameters.abrTipCol ? String(normalizedParameters.abrTipCol) : '1',
    iniPer: normalizedParameters.iniPer || '01/01/2021',
    encryption: getSettingValue(settings, 'encryption') != null ? String(getSettingValue(settings, 'encryption')) : '0',
    mode: integration.mode || 'scheduled',
    days_sync_at: integration.days_sync_at || 1,
    active: integration.active !== undefined ? integration.active : true,
    auto_team_link: Boolean(getSettingValue(settings, 'team') || normalizedParameters.team || ''),
    team_name_field: String(getSettingValue(settings, 'team') || normalizedParameters.team || ''),
    custom_fields: normalizeCustomFields(getSettingValue(settings, 'custom_fields')),
  };
};

export default function SettingIntegration() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [integrationId, setIntegrationId] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [showInitialPassword, setShowInitialPassword] = useState(false);
  const [availableCustomFields, setAvailableCustomFields] = useState([]);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState(false);

  const isKiwifySelected = useMemo(
    () => selectedPlatform === 'kiwify',
    [selectedPlatform]
  );
  const isSultsSelected = useMemo(
    () => selectedPlatform === 'sults',
    [selectedPlatform]
  );
  const isTotvsSelected = useMemo(
    () => selectedPlatform === 'totvs',
    [selectedPlatform]
  );
  const selectedConfig = selectedPlatform
    ? PLATFORM_CONFIG[selectedPlatform]
    : null;
  const isIntegrationActive = Boolean(formData.active);
  const usesTokenAuth = selectedConfig?.authType === 'token';

  useEffect(() => {
    if (selectedPlatform !== 'sults' && selectedPlatform !== 'totvs') return undefined;

    let active = true;
    setAvailableCustomFields([]);
    setCustomFieldsError(false);
    setCustomFieldsLoading(true);

    CustomFieldsData.getCustomFields()
      .then(({ data }) => {
        if (!active) return;
        const rawFields = data?.custom_fields || [];
        const fields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
        if (!Array.isArray(fields)) throw new Error('Formato inválido de campos customizados.');
        setAvailableCustomFields([
          ...new Set(fields.map(field => String(field?.fieldName || '').trim()).filter(Boolean)),
        ]);
      })
      .catch(() => {
        if (active) setCustomFieldsError(true);
      })
      .finally(() => {
        if (active) setCustomFieldsLoading(false);
      });

    return () => { active = false; };
  }, [selectedPlatform]);

  const integrationCards = useMemo(
    () => [
      ...Object.keys(PLATFORM_CONFIG).map((key) => ({
        key,
        title: PLATFORM_CONFIG[key].title,
        description: PLATFORM_CONFIG[key].description,
      })),
      // {
      //   key: 'kiwify',
      //   title: 'Kiwify',
      //   description: 'Integração de pagamentos e webhooks (em breve).',
      // },
      // {
      //   key: 'totvs',
      //   title: 'TOTVS',
      //   description: 'Integração com ERP TOTVS para dados de colaboradores (em breve).',
      // },
    ],
    []
  );

  const loadIntegration = useCallback(async (platform) => {
    try {
      setLoading(true);
      const response = await IntegrationData.listIntegrations();
      const integrations = response?.data || [];
      const selectedIntegration = integrations.find(
        (integration) => integration.platform === platform
      );

      if (selectedIntegration) {
        setIntegrationId(selectedIntegration.id);
        setFormData({
          ...fromIntegration(selectedIntegration),
        });
        setLastSyncAt(selectedIntegration.last_sync_at || null);
      } else {
        setIntegrationId(null);
        setFormData(getDefaultForm(platform));
        setLastSyncAt(null);
      }
    } catch (error) {
      toast.error('Erro ao carregar integrações.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectPlatform = useCallback(
    async (platform) => {
      if (selectedPlatform === platform) {
        setSelectedPlatform(null);
        return;
      }

      setSelectedPlatform(platform);

      if (PLATFORM_CONFIG[platform]) {
        await loadIntegration(platform);
      }
    },
    [loadIntegration, selectedPlatform]
  );

  const handleChange = (field) => (event) => {
    const isSwitchField = field === 'active' || field === 'auto_team_link' || field === 'use_cpf_as_password' || field === 'send_welcome_emails';
    const value = isSwitchField ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'auto_team_link' && !value ? { team_name_field: '' } : {}),
    }));
  };

  const handleCustomFieldChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      custom_fields: (prev.custom_fields || []).map(
        (customField, currentIndex) =>
          currentIndex === index
            ? {
                ...customField,
                [field]: value,
              }
            : customField
      ),
    }));
  };

  const handleAddCustomField = () => {
    setFormData((prev) => ({
      ...prev,
      custom_fields: [...(prev.custom_fields || []), EMPTY_CUSTOM_FIELD],
    }));
  };

  const handleRemoveCustomField = (index) => {
    setFormData((prev) => {
      const customFields = (prev.custom_fields || []).filter(
        (_, currentIndex) => currentIndex !== index
      );

      return {
        ...prev,
        custom_fields: customFields.length
          ? customFields
          : [EMPTY_CUSTOM_FIELD],
      };
    });
  };

  const formattedLastSyncAt = useMemo(() => {
    if (!lastSyncAt) return null;
    const parsedDate = new Date(lastSyncAt);
    if (Number.isNaN(parsedDate.getTime())) return null;

    return parsedDate.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [lastSyncAt]);

  const buildPayload = useCallback(() => {
    const platformConfig =
      PLATFORM_CONFIG[selectedPlatform] || PLATFORM_CONFIG.senior;
    const { settings } = toSettings(formData, selectedPlatform);

    return {
      type: platformConfig.type,
      platform: selectedPlatform,
      direction: platformConfig.direction,
      mode: formData.mode,
      settings: JSON.stringify(settings),
      active: Boolean(formData.active),
      days_sync_at: Number(formData.days_sync_at || 1),
    };
  }, [formData, selectedPlatform]);

  const upsertIntegration = useCallback(async () => {
    const payload = buildPayload();
    const parsedSettings =
      typeof payload.settings === 'string'
        ? JSON.parse(payload.settings)
        : payload.settings;
    const baseUrl = parsedSettings.find(
      (item) => item.fieldName === 'base_url'
    )?.fieldValue;
    const user = parsedSettings.find(
      (item) => item.fieldName === 'user'
    )?.fieldValue;
    const password = parsedSettings.find(
      (item) => item.fieldName === 'password'
    )?.fieldValue;
    const token = parsedSettings.find(
      (item) => item.fieldName === 'token'
    )?.fieldValue;

    if (!baseUrl) {
      throw new Error('Preencha Base URL para continuar.');
    }

    if (PLATFORM_CONFIG[selectedPlatform]?.authType === 'token' && !token) {
      throw new Error('Preencha o Token para continuar.');
    }

    if (PLATFORM_CONFIG[selectedPlatform]?.authType === 'password' && (!user || !password)) {
      throw new Error('Preencha Base URL, Usuário e Senha para continuar.');
    }

    if (
      formData.auto_team_link &&
      !String(formData.team_name_field || '').trim()
    ) {
      throw new Error(
        'Informe o campo referente ao nome do time (ex.: nomCcu).'
      );
    }

    const hasIncompleteCustomField = (formData.custom_fields || []).some(
      (field) =>
        Boolean(field.source_field || field.target_field) &&
        (!field.source_field || !field.target_field)
    );

    if ((selectedPlatform === 'sults' || selectedPlatform === 'totvs') && hasIncompleteCustomField) {
      throw new Error(
        'Selecione o campo customizado da plataforma para cada campo de origem.'
      );
    }

    if (
      (selectedPlatform === 'sults' || selectedPlatform === 'totvs') &&
      (formData.custom_fields || []).some(field => field.source_field && field.target_field) &&
      (customFieldsLoading || customFieldsError ||
        (formData.custom_fields || []).some(field =>
          field.target_field && !availableCustomFields.includes(field.target_field)
        ))
    ) {
      throw new Error('Selecione um campo customizado cadastrado na empresa para cada mapeamento.');
    }

    if (integrationId) {
      const response = await IntegrationData.updateIntegration(
        integrationId,
        payload
      );
      return response?.data;
    }

    const response = await IntegrationData.createIntegration(payload);
    return response?.data;
  }, [
    buildPayload,
    formData.auto_team_link,
    formData.custom_fields,
    formData.team_name_field,
    availableCustomFields,
    customFieldsLoading,
    customFieldsError,
    integrationId,
    selectedPlatform,
  ]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const integration = await upsertIntegration();
      if (integration?.id) {
        setIntegrationId(integration.id);
      }
      toast.success('Integração salva com sucesso.');
    } catch (error) {
      toast.error(error?.message || 'Erro ao salvar integração.');
    } finally {
      setSaving(false);
    }
  }, [upsertIntegration]);

  const handleTestConnection = useCallback(async () => {
    try {
      if (!selectedConfig?.canTestConnection) {
        toast.info(
          'Teste de conexão disponível após implementação do backend.'
        );
        return;
      }

      setTesting(true);
      const integration = await upsertIntegration();
      if (integration?.id) {
        setIntegrationId(integration.id);
      }
      const idToRun = integration?.id || integrationId;

      if (!idToRun) {
        throw new Error(
          'Não foi possível identificar a integração para teste.'
        );
      }

      const response = await IntegrationData.runIntegration(idToRun, {
        sampleLimit: 5,
      });
      const result = response?.data?.result || {};
      const usersSummary = result?.users_summary || {};
      const totalUsersProcessed = isTotvsSelected
        ? Number(result.total_colaboradores || 0)
        : Number(usersSummary.created || 0) + Number(usersSummary.existing || 0);

      toast.success(
        `Conectado com Sucesso!! 🚀  Usuários encontrados: ${totalUsersProcessed}`
      );
    } catch (error) {
      toast.error(error?.message || 'Erro ao testar integração.');
    } finally {
      setTesting(false);
    }
  }, [integrationId, isTotvsSelected, selectedConfig, upsertIntegration]);

  const handleSyncAll = useCallback(async () => {
    try {
      setSyncing(true);
      const integration = await upsertIntegration();
      if (integration?.id) {
        setIntegrationId(integration.id);
      }
      const idToRun = integration?.id || integrationId;

      if (!idToRun) {
        throw new Error(
          'Não foi possível identificar a integração para sincronizar.'
        );
      }

      const response = await IntegrationData.runIntegration(idToRun);
      const result = response?.data?.result || {};
      const usersSummary = result?.users_summary || {};
      const totalUsersProcessed =
        Number(usersSummary.created || 0) + Number(usersSummary.existing || 0);
      const updatedCustomFields = Number(usersSummary.updatedCustomFields || 0);
      const welcomeEmailsSent = Number(usersSummary.welcomeEmailsSent || 0);

      toast.success(
        `Sincronização concluída. Usuários processados: ${totalUsersProcessed}. Campos customizados atualizados: ${updatedCustomFields}. E-mails enviados: ${welcomeEmailsSent}`
      );
      if (Number(usersSummary.emailConflicts || 0) > 0) {
        toast.warn(
          `${usersSummary.emailConflicts} registro(s) ignorado(s): e-mail já vinculado a outro CPF.`
        );
      }
      setLastSyncAt(new Date().toISOString());
    } catch (error) {
      toast.error(error?.message || 'Erro ao sincronizar integração.');
    } finally {
      setSyncing(false);
    }
  }, [integrationId, upsertIntegration]);

  const testButtonLabel = (() => {
    if (!selectedConfig?.canTestConnection) {
      return 'Teste em breve';
    }

    return testing ? 'Testando...' : 'Testar conexão';
  })();

  return (
    <div className={classes.root}>
      <Grid container spacing={2} className={classes.cardGrid}>
        {integrationCards.map((card) => (
          <Grid item xs={12} md={4} key={card.key}>
            <Card
              className={clsx(classes.optionCard, {
                [classes.optionCardActive]: selectedPlatform === card.key,
              })}
              onClick={() => handleSelectPlatform(card.key)}
            >
              <CardContent>
                <div className={classes.optionHeader}>
                  <LinkIcon color="primary" />
                  <Typography variant="h6">{card.title}</Typography>
                </div>
                <Typography variant="body2" className={classes.helperText}>
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!selectedPlatform && (
        <Card>
          <CardContent>
            <Typography variant="body1">
              Selecione uma integração acima para abrir as configurações.
            </Typography>
          </CardContent>
        </Card>
      )}

      {selectedConfig && (
        <Card>
          <CardHeader
            title={selectedConfig.formTitle}
            action={
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={isIntegrationActive}
                    onChange={handleChange('active')}
                  />
                }
                label="Integração ativa"
              />
            }
          />
          <Divider />

          {loading ? (
            <CardContent>
              <CircularProgress size={26} />
            </CardContent>
          ) : (
            <>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <div className={classes.sectionTitle}>
                      <LinkIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle1">Conexão</Typography>
                    </div>
                    {formattedLastSyncAt && (
                      <Typography
                        variant="body2"
                        className={classes.helperText}
                      >
                        Última sincronização: {formattedLastSyncAt}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Base URL"
                      value={formData.base_url}
                      onChange={handleChange('base_url')}
                      disabled={!isIntegrationActive}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider className={classes.sectionDivider} />
                  </Grid>

                  <Grid item xs={12}>
                    <div className={classes.sectionTitle}>
                      <VpnKey fontSize="small" color="primary" />
                      <Typography variant="subtitle1">Autenticação</Typography>
                    </div>
                  </Grid>

                  {usesTokenAuth ? (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        label="Token"
                        value={formData.token}
                        onChange={handleChange('token')}
                        disabled={!isIntegrationActive}
                        helperText="Informe o token de autenticação fornecido pela Sults."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle token visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ padding: 5 }}
                                disabled={!isIntegrationActive}
                              >
                                {showPassword ? (
                                  <Visibility fontSize="small" />
                                ) : (
                                  <VisibilityOff fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Usuário"
                          value={formData.user}
                          onChange={handleChange('user')}
                          disabled={!isIntegrationActive}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined"
                          label="Senha"
                          value={formData.password}
                          onChange={handleChange('password')}
                          disabled={!isIntegrationActive}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword(!showPassword)}
                                  style={{ padding: 5 }}
                                  disabled={!isIntegrationActive}
                                >
                                  {showPassword ? (
                                    <Visibility fontSize="small" />
                                  ) : (
                                    <VisibilityOff fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider className={classes.sectionDivider} />
                  </Grid>

                  <Grid item xs={12}>
                    <div className={classes.sectionTitle}>
                      <Settings fontSize="small" color="primary" />
                      <Typography variant="subtitle1">Configurações</Typography>
                    </div>
                  </Grid>

                  {!usesTokenAuth && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Empresa (numEmp)"
                        value={formData.numEmp}
                        onChange={handleChange('numEmp')}
                        disabled={!isIntegrationActive}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={showInitialPassword ? 'text' : 'password'}
                      label="Senha inicial dos usuários (opcional)"
                      value={formData.initial_password}
                      onChange={handleChange('initial_password')}
                      disabled={!isIntegrationActive}
                      helperText="Se preenchido, os colaboradores importados utilizarão esta senha no primeiro acesso."
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle initial password visibility"
                              onClick={() =>
                                setShowInitialPassword(!showInitialPassword)
                              }
                              style={{ padding: 5 }}
                              disabled={!isIntegrationActive}
                            >
                              {showInitialPassword ? (
                                <Visibility fontSize="small" />
                              ) : (
                                <VisibilityOff fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={Boolean(formData.use_cpf_as_password)}
                          onChange={handleChange('use_cpf_as_password')}
                          disabled={!isIntegrationActive}
                        />
                      }
                      label="Usar CPF do usuário como senha"
                    />
                  </Grid>

                  {isTotvsSelected && (
                    <Grid item xs={12}>
                      <Typography variant="body2" className={classes.helperText}>
                        Situação define quais consultas serão executadas, inclusive no agendamento. Ativos e demitidos faz duas consultas separadas. As datas abaixo valem para a execução manual; no agendamento, o período começa na última sincronização concluída. Sem sincronização anterior, a primeira consulta busca o histórico completo. Datas no formato DD/MM/AAAA.
                      </Typography>
                      <Grid container spacing={2} style={{ marginTop: 4 }}>
                        {[
                          ['data_admissao_inicio', 'Admissão inicial'],
                          ['data_admissao_fim', 'Admissão final'],
                          ['data_demissao_inicio', 'Demissão inicial'],
                          ['data_demissao_fim', 'Demissão final'],
                        ].map(([field, label]) => (
                          <Grid item xs={12} md={3} key={field}>
                            <TextField fullWidth variant="outlined" label={label} placeholder="DD/MM/AAAA" value={formData[field]} onChange={handleChange(field)} disabled={!isIntegrationActive} />
                          </Grid>
                        ))}
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth variant="outlined" select label="Situação" value={formData.situacao} onChange={handleChange('situacao')} disabled={!isIntegrationActive} SelectProps={{ native: true }}>
                            <option value="AD">Ativos e demitidos (duas consultas)</option>
                            <option value="A">A - Ativo</option>
                            <option value="D">D - Demitido</option>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={Boolean(formData.send_welcome_emails)}
                                onChange={handleChange('send_welcome_emails')}
                                disabled={!isIntegrationActive}
                              />
                            }
                            label="Enviar e-mail de boas-vindas aos novos usuários"
                          />
                          <Typography variant="body2" className={classes.helperText}>
                            Desligado por padrão: os usuários são cadastrados sem envio de e-mail.
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  {!usesTokenAuth && (
                    <Grid item xs={12}>
                      <Accordion disabled={!isIntegrationActive}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">
                            Opções avançadas
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Tipo Colaborador (abrTipCol)"
                                value={formData.abrTipCol}
                                onChange={handleChange('abrTipCol')}
                                disabled={!isIntegrationActive}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Data inicial (iniPer)"
                                placeholder="DD/MM/AAAA"
                                value={formData.iniPer}
                                onChange={handleChange('iniPer')}
                                disabled={!isIntegrationActive}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Encryption"
                                value={formData.encryption}
                                onChange={handleChange('encryption')}
                                disabled={!isIntegrationActive}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {usesTokenAuth && (
                    <Grid item xs={12}>
                      <Accordion disabled={!isIntegrationActive}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">
                            Opções avançadas
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                select
                                label="Modo"
                                value={formData.mode}
                                onChange={handleChange('mode')}
                                disabled={!isIntegrationActive}
                                helperText="Define se a integração será rodada pelo job ou manualmente."
                                SelectProps={{ native: true }}
                              >
                                <option value="scheduled">Agendado</option>
                                <option value="realtime">Tempo real</option>
                                <option value="batch">Lote</option>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                select
                                label="Frequência em dias"
                                value={formData.days_sync_at}
                                onChange={handleChange('days_sync_at')}
                                disabled={!isIntegrationActive}
                                helperText="Valores aceitos hoje no backend: 1, 3 ou 7."
                                SelectProps={{ native: true }}
                              >
                                <option value={1}>1 dia</option>
                                <option value={3}>3 dias</option>
                                <option value={7}>7 dias</option>
                              </TextField>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {(isSultsSelected || isTotvsSelected) && (
                    <Grid item xs={12}>
                      <Divider className={classes.sectionDivider} />
                      <div className={classes.sectionTitle}>
                        <Settings fontSize="small" color="primary" />
                        <Typography variant="subtitle1">
                          Campos customizados
                        </Typography>
                      </div>
                      <Typography
                        variant="body2"
                        className={classes.helperText}
                      >
                        Relacione o campo retornado pela {isTotvsSelected ? 'TOTVS RM' : 'Sults'} com o campo
                        customizado salvo no cadastro do usuário.
                      </Typography>

                      {(formData.custom_fields || []).map((field, index) => (
                        <Grid
                          container
                          spacing={2}
                          alignItems="flex-start"
                          key={`${field.source_field}-${index}`}
                          className={classes.customFieldRow}
                        >
                          <Grid item xs={12} md={5}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={isTotvsSelected ? 'Campo do TOTVS RM' : 'Campo da Sults'}
                              placeholder={isTotvsSelected ? 'Ex.: NOMESETOR' : 'Ex.: empresa.nomeFantasia'}
                              value={field.source_field}
                              onChange={handleCustomFieldChange(
                                index,
                                'source_field'
                              )}
                              disabled={!isIntegrationActive}
                              helperText={isTotvsSelected ? 'Informe o nome exato retornado pelo RM. Ex.: NOMESETOR' : 'Use caminho com ponto. Ex.: empresa.nomeFantasia'}
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              select
                              label="Campo customizado da plataforma"
                              value={field.target_field}
                              onChange={handleCustomFieldChange(
                                index,
                                'target_field'
                              )}
                              disabled={
                                !isIntegrationActive ||
                                customFieldsLoading ||
                                customFieldsError ||
                                !availableCustomFields.length
                              }
                              helperText={
                                customFieldsError
                                  ? 'Não foi possível carregar os campos da empresa.'
                                  : customFieldsLoading
                                    ? 'Carregando campos...'
                                    : availableCustomFields.length
                                      ? 'Selecione um campo cadastrado na empresa.'
                                      : 'Nenhum campo customizado cadastrado na empresa.'
                              }
                              SelectProps={{ native: true }}
                            >
                              <option value="">Selecione um campo</option>
                              {field.target_field &&
                                !availableCustomFields.includes(field.target_field) && (
                                  <option value={field.target_field} disabled>
                                    {field.target_field} (não cadastrado)
                                  </option>
                                )}
                              {availableCustomFields.map(fieldName => (
                                <option key={fieldName} value={fieldName}>
                                  {fieldName}
                                </option>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={2}
                            className={classes.customFieldRemove}
                          >
                            <IconButton
                              aria-label="remove custom field"
                              onClick={() => handleRemoveCustomField(index)}
                              disabled={!isIntegrationActive}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}

                      <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={handleAddCustomField}
                        disabled={!isIntegrationActive}
                        startIcon={<Add />}
                        style={{ marginTop: 12 }}
                      >
                        Adicionar campo
                      </Button>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider className={classes.sectionDivider} />
                  </Grid>

                  <Grid item xs={12}>
                    <div className={classes.sectionTitle}>
                      <Group fontSize="small" color="primary" />
                      <Typography variant="subtitle1">Times</Typography>
                    </div>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={Boolean(formData.auto_team_link)}
                          onChange={handleChange('auto_team_link')}
                          disabled={!isIntegrationActive}
                        />
                      }
                      label="Vincular colaboradores a times automaticamente"
                    />

                    {formData.auto_team_link && (
                      <Grid item xs={12} md={12}>
                        <TextField
                          fullWidth
                          style={{ marginTop: 12 }}
                          variant="outlined"
                          label="Campo referente ao nome do time"
                          placeholder="Ex.: nameCcu"
                          value={formData.team_name_field}
                          onChange={handleChange('team_name_field')}
                          disabled={!isIntegrationActive}
                          helperText={
                            <>
                              Informe o nome do campo retornado pelo sistema que
                              representará o time do colaborador. (ex: nomCcu,
                              department, etc).
                              <br />
                              ⚠️ Esse campo deve existir no retorno da
                              integração — caso contrário, o vínculo com o time
                              não será realizado automaticamente.
                            </>
                          }
                          inputProps={{
                            list: 'team-field-suggestions',
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />
              <CardActions className={classes.actions}>
                <Button
                  color="default"
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={
                    testing ||
                    saving ||
                    syncing ||
                    !isIntegrationActive ||
                    !selectedConfig.canTestConnection
                  }
                  title={
                    selectedConfig.canTestConnection
                      ? ''
                      : 'Teste disponível após implementação do backend.'
                  }
                  startIcon={<PlayCircleOutline />}
                >
                  {testButtonLabel}
                </Button>

                <div className={classes.rightActions}>
                  {usesTokenAuth && (
                    <Button
                      color="default"
                      variant="outlined"
                      onClick={handleSyncAll}
                      disabled={
                        saving ||
                        testing ||
                        syncing ||
                        !isIntegrationActive ||
                        Boolean(lastSyncAt)
                      }
                      startIcon={<PlayCircleOutline />}
                      title={
                        lastSyncAt
                          ? `Sincronização já executada em ${formattedLastSyncAt}.`
                          : ''
                      }
                    >
                      {syncing ? 'Sincronizando...' : 'Sincronizar tudo'}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || testing || syncing}
                    startIcon={<Save />}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </CardActions>
            </>
          )}
        </Card>
      )}

      {isKiwifySelected && (
        <Card>
          <CardHeader title="Integração - Kiwify" />
          <Divider />
          <CardContent>
            <Typography variant="body1">Configuração em breve.</Typography>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
